/**
 * Orte — Werke, Kieswerke, Baustellen.
 *
 * Hier entscheidet sich, ob aus 128 Orten wieder 214 Schreibweisen werden.
 * Deshalb sind drei Dinge sichtbar, die man in einer schlichten Stammdatenliste
 * nicht sähe:
 *
 * Der Haken „bestätigt". Ein Ort, den ein Fahrer unterwegs angelegt hat, ist
 * bis zum Blick des Büros unbestätigt. Das ist keine Abwertung, sondern eine
 * Arbeitsliste.
 *
 * Die alten Schreibweisen. Wurde etwas zusammengeführt, steht darunter, unter
 * welchen Namen der Ort früher lief — sonst sucht jemand danach und legt ihn
 * ein drittes Mal an.
 *
 * Zusammenführen direkt in der Zeile. Nicht versteckt unter „erweitert": Der
 * Moment, in dem man eine Dublette erkennt, ist der Moment, in dem man sie
 * auflösen können muss.
 */

import { useMemo, useState } from 'react';
import { Seitenkopf } from '../Rahmen';
import {
  Fehlerkasten,
  Karte,
  Knopf,
  Marker,
  Suchleiste,
  Tabellenkarte,
  Textfeld,
} from '../gestaltung/bausteine';
import { useAbfrage } from '../daten/abfrage';
import { Speicherfehler } from '../daten/speichern';
import { alsKlarfehler, type Klarfehler } from '../daten/fehler';
import { passt } from '../daten/textvergleich';
import { anschriftszeile } from '../daten/anschrift';
import {
  ortAendern,
  ortAnlegen,
  orteLaden,
  ortZusammenfuehren,
  type Ort,
} from '../daten/stammdaten';
import { ZusammenfuehrenFormular } from './Zusammenfuehren';
import { Detailtafel, type Detailfeld } from '../gestaltung/Detailtafel';
import { useFormularOffen, useEntwurfsfeld } from '../daten/formularEntwurf';

const ORTFELDER: Detailfeld[] = [
  { schluessel: 'name', marke: 'Name' },
  { schluessel: 'strasse', marke: 'Straße' },
  { schluessel: 'plz', marke: 'PLZ' },
  { schluessel: 'ortsname', marke: 'Ortschaft' },
];

export function OrteSeite() {
  const liste = useAbfrage(orteLaden, []);
  const [suche, setSuche] = useState('');
  // Offen bleibt offen — auch über Seitenwechsel und Neuladen (HJ-025).
  const [formularOffen, setFormularOffen] = useFormularOffen('ort-neu');
  const [zusammen, setZusammen] = useState<Ort | null>(null);
  const [offen, setOffen] = useState<Ort | null>(null);
  const [fehler, setFehler] = useState<Klarfehler | null>(null);
  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);

  const gefiltert = useMemo(() => {
    const alle = liste.daten ?? [];
    if (!suche.trim()) return alle;
    return alle.filter(
      (o) =>
        passt(o.name, suche) ||
        passt(o.ortsname ?? '', suche) ||
        o.alt_schreibweisen.some((alt) => passt(alt, suche)),
    );
  }, [liste.daten, suche]);

  async function mitFehlern(schluessel: string, tun: () => Promise<void>) {
    setFehler(null);
    setBeschaeftigt(schluessel);
    try {
      await tun();
    } catch (problem) {
      setFehler(
        problem instanceof Speicherfehler ? problem.klar : alsKlarfehler(problem),
      );
    } finally {
      setBeschaeftigt(null);
    }
  }

  return (
    <>
      <Seitenkopf
        titel="Orte"
        erklaerung="Alles, was be- oder entladen wird. Doppelte Schreibweisen lassen sich hier zusammenführen — alles, was am alten Namen hing, hängt danach am neuen."
        werkzeuge={
          <Knopf art="haupt" onClick={() => setFormularOffen(!formularOffen)}>
            {formularOffen ? 'Abbrechen' : 'Ort anlegen'}
          </Knopf>
        }
      />

      {fehler && (
        <div style={{ marginBottom: 16 }}>
          <Fehlerkasten fehler={fehler} />
        </div>
      )}

      {offen && (
        <div style={{ marginBottom: 16 }}>
          <Detailtafel
            key={offen.id}
            titel={offen.name}
            felder={ORTFELDER}
            laeuft={beschaeftigt === offen.id}
            werte={Object.fromEntries(
              ORTFELDER.map((f) => [
                f.schluessel,
                String((offen as unknown as Record<string, unknown>)[f.schluessel] ?? ''),
              ]),
            )}
            hinweise={
              offen.alt_schreibweisen.length > 0 ? (
                <p className="meta">
                  Frühere Schreibweisen: {offen.alt_schreibweisen.join(' · ')}
                </p>
              ) : null
            }
            schliessen={() => setOffen(null)}
            speichern={(geaendert) =>
              void mitFehlern(offen.id, async () => {
                const werte: Record<string, unknown> = {};
                for (const [schluessel, wert] of Object.entries(geaendert)) {
                  werte[schluessel] = wert.trim() === '' ? null : wert.trim();
                }
                await ortAendern(offen, werte as Partial<Ort>);
                setOffen(null);
                liste.auffrischen();
              })
            }
          />
        </div>
      )}

      {zusammen && (
        <div style={{ marginBottom: 16 }}>
          {/* Der Schlüssel wirft das Formular beim Wechsel der Quelle weg.
              Sonst bleibt das vorhin gewählte ZIEL stehen, und der nächste Ort
              wird still in den falschen zusammengeführt. */}
          <ZusammenfuehrenFormular
            key={zusammen.id}
            quelle={zusammen}
            auswahl={liste.daten ?? []}
            wortFuerEinen="Ort"
            laeuft={beschaeftigt === `merge:${zusammen.id}`}
            abbrechen={() => setZusammen(null)}
            bestaetigen={(zielId) =>
              void mitFehlern(`merge:${zusammen.id}`, async () => {
                await ortZusammenfuehren(zusammen.id, zielId);
                setZusammen(null);
                liste.auffrischen();
              })
            }
          />
        </div>
      )}

      {formularOffen && (
        <div style={{ marginBottom: 16 }}>
          <AnlegenFormular
            vorhandene={liste.daten ?? []}
            fertig={() => {
              setFormularOffen(false);
              liste.auffrischen();
            }}
          />
        </div>
      )}

      <Tabellenkarte
        titel="Alle Orte"
        werkzeuge={
          <Suchleiste
            wert={suche}
            setzen={setSuche}
            platzhalter="Name, Ortschaft oder alte Schreibweise"
            anzahl={`${gefiltert.length} von ${(liste.daten ?? []).length}`}
          />
        }
        laedt={liste.laedt}
        fehler={liste.fehler}
        nochmal={liste.auffrischen}
        eintraege={gefiltert}
        spalten={[
          { text: 'Name' },
          { text: 'Anschrift' },
          { text: 'Status' },
          { text: '', rechts: true },
        ]}
        leer={suche ? 'Kein Ort passt zu dieser Suche.' : 'Noch kein Ort angelegt.'}
        zeile={(ort) => (
          <tr
            key={ort.id}
            className={ort.aktiv ? undefined : 'stillgelegt'}
            aria-current={offen?.id === ort.id ? 'true' : undefined}
          >
            <td>
              <button className="zeilenlink" onClick={() => setOffen(ort)}>
                {ort.name}
              </button>
              {ort.alt_schreibweisen.length > 0 && (
                <div className="meta">
                  früher: {ort.alt_schreibweisen.join(' · ')}
                </div>
              )}
            </td>
            <td>
              {[ort.strasse, [ort.plz, ort.ortsname].filter(Boolean).join(' ')]
                .filter(Boolean)
                .join(', ') || '—'}
            </td>
            <td>
              <div className="reihe" style={{ gap: 6 }}>
                {!ort.bestaetigt && <Marker art="warn">unbestätigt</Marker>}
                {!ort.aktiv && <Marker art="neutral">stillgelegt</Marker>}
                {ort.bestaetigt && ort.aktiv && <Marker art="gut">geprüft</Marker>}
              </div>
            </td>
            <td className="tabelle__rechts">
              <div className="reihe reihe--rechts">
                {!ort.bestaetigt && (
                  <Knopf
                    art="still"
                    klein
                    disabled={beschaeftigt === ort.id}
                    onClick={() =>
                      void mitFehlern(ort.id, async () => {
                        await ortAendern(ort, { bestaetigt: true });
                        liste.auffrischen();
                      })
                    }
                  >
                    Bestätigen
                  </Knopf>
                )}
                <Knopf art="still" klein onClick={() => setZusammen(ort)}>
                  Zusammenführen
                </Knopf>
                <Knopf
                  art="still"
                  klein
                  disabled={beschaeftigt === ort.id}
                  onClick={() =>
                    void mitFehlern(ort.id, async () => {
                      await ortAendern(ort, { aktiv: !ort.aktiv });
                      liste.auffrischen();
                    })
                  }
                >
                  {ort.aktiv ? 'Stilllegen' : 'Zurückholen'}
                </Knopf>
              </div>
            </td>
          </tr>
        )}
      />
    </>
  );
}

/**
 * Beim Anlegen wird gewarnt, bevor eine Dublette entsteht — dieselbe Rückfrage
 * wie in der App („Meinst du eventuell …"). Hinterher kostet sie Büroarbeit,
 * hier kostet sie einen Blick.
 */
function AnlegenFormular(eigenschaften: {
  vorhandene: Ort[];
  fertig: () => void;
}) {
  const [name, setName] = useEntwurfsfeld('ort-neu', 'name', '');
  const [strasse, setStrasse] = useEntwurfsfeld('ort-neu', 'strasse', '');
  const [plz, setPlz] = useEntwurfsfeld('ort-neu', 'plz', '');
  const [ortsname, setOrtsname] = useEntwurfsfeld('ort-neu', 'ortsname', '');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Klarfehler | null>(null);

  const aehnliche = useMemo(() => {
    if (name.trim().length < 3) return [];
    return eigenschaften.vorhandene.filter((o) => passt(o.name, name)).slice(0, 4);
  }, [eigenschaften.vorhandene, name]);

  async function speichern() {
    if (!name.trim() || laeuft) return;
    setLaeuft(true);
    setFehler(null);
    try {
      await ortAnlegen({ name, strasse, plz, ortsname });
      eigenschaften.fertig();
    } catch (problem) {
      setFehler(
        problem instanceof Speicherfehler ? problem.klar : alsKlarfehler(problem),
      );
      setLaeuft(false);
    }
  }

  return (
    <Karte titel="Neuer Ort">
      <div className="stapel">
        {fehler && <Fehlerkasten fehler={fehler} />}
        <div className="feldreihe">
          <Textfeld marke="Name" wert={name} setzen={setName} autoFocus />
          <Textfeld marke="Straße" wert={strasse} setzen={setStrasse} />
          <Textfeld marke="PLZ" wert={plz} setzen={setPlz} />
          <Textfeld marke="Ortschaft" wert={ortsname} setzen={setOrtsname} />
        </div>

        {aehnliche.length > 0 && (
          <div className="meldung meldung--warnung">
            <div>
              <strong>Ähnliche Einträge gefunden – prüfe, ob es ihn schon gibt.</strong>
              {aehnliche
                .map((o) => {
                  const anschrift = anschriftszeile(o.strasse, o.plz, o.ortsname);
                  return anschrift ? `${o.name} (${anschrift})` : o.name;
                })
                .join(' · ')}
            </div>
          </div>
        )}

        <div className="reihe reihe--rechts">
          <Knopf art="still" onClick={eigenschaften.fertig}>
            Abbrechen
          </Knopf>
          <Knopf
            art="haupt"
            disabled={!name.trim() || laeuft}
            onClick={() => void speichern()}
          >
            {laeuft
              ? 'Wird angelegt …'
              : aehnliche.length > 0
                ? 'Ist neu – anlegen'
                : 'Anlegen'}
          </Knopf>
        </div>
      </div>
    </Karte>
  );
}
